// Connexion hors-ligne : mémorise uniquement une empreinte PBKDF2 pour valider l'accès local.
// Aucun mot de passe, jeton d'accès ou jeton de rafraîchissement n'est dupliqué dans ce registre.

import { secureStorage } from './secureStorage';
import { BACKEND_URL } from './backendUrl';

const CREDS_KEY = 'offline_creds_v1';
const LEGACY_KEY_DB = 'racinefit_secure';
const ITERATIONS = 150000;
const enc = new TextEncoder();

const norm = (u) => (u || '').trim().toLowerCase();

const bufToHex = (buf) =>
  [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');

const hexToBuf = (hex) => {
  const a = new Uint8Array(hex.length / 2);
  for (let i = 0; i < a.length; i++) a[i] = parseInt(hex.substr(i * 2, 2), 16);
  return a;
};

const subtle = () => (typeof crypto !== 'undefined' && crypto.subtle) ? crypto.subtle : null;


async function pbkdf2(password, saltBytes, iterations = ITERATIONS) {
  const s = subtle();
  if (!s) throw new Error('WebCrypto indisponible (contexte non sécurisé)');
  const keyMaterial = await s.importKey('raw', enc.encode(password), 'PBKDF2', false, ['deriveBits']);
  const bits = await s.deriveBits(
    { name: 'PBKDF2', salt: saltBytes, iterations, hash: 'SHA-256' },
    keyMaterial,
    256
  );
  return bufToHex(bits);
}

const safeCredential = (record) => {
  if (!record || typeof record !== 'object' || !record.salt || !record.hash) return null;
  return {
    username: typeof record.username === 'string' ? record.username : '',
    salt: record.salt,
    iterations: Number.isInteger(record.iterations) ? record.iterations : ITERATIONS,
    hash: record.hash,
    user: record.user && typeof record.user === 'object' ? record.user : null,
    savedAt: Number.isFinite(record.savedAt) ? record.savedAt : null,
  };
};

const purgeLegacyCryptoKey = () => {
  try { indexedDB.deleteDatabase(LEGACY_KEY_DB); } catch { /* IndexedDB indisponible */ }
};

export function loadCreds() {
  try {
    const stored = JSON.parse(localStorage.getItem(CREDS_KEY) || '{}');
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
    const sanitized = Object.fromEntries(Object.entries(stored).map(([key, record]) => [key, safeCredential(record)]).filter(([, record]) => record));
    if (JSON.stringify(stored) !== JSON.stringify(sanitized)) localStorage.setItem(CREDS_KEY, JSON.stringify(sanitized));
    purgeLegacyCryptoKey();
    return sanitized;
  } catch {
    return {};
  }
}

function saveCreds(map) {
  try {
    const sanitized = Object.fromEntries(Object.entries(map || {}).map(([key, record]) => [key, safeCredential(record)]).filter(([, record]) => record));
    localStorage.setItem(CREDS_KEY, JSON.stringify(sanitized));
    purgeLegacyCryptoKey();
  } catch {}
}

if (typeof window !== 'undefined') loadCreds();

export function hasAnyCreds() {
  return Object.keys(loadCreds()).length > 0;
}

// Liste les comptes mémorisés hors-ligne sur cet appareil (sans données sensibles).
export function listCreds() {
  const map = loadCreds();
  return Object.entries(map).map(([key, rec]) => ({
    key,
    username: rec.username || key,
    role: rec.user?.role || null,
    fullName: rec.user?.full_name || rec.user?.name || null,
    savedAt: rec.savedAt || null,
  }));
}

// Retire un compte mémorisé (par clé normalisée). Renvoie true si supprimé.
export function forgetCredential(key) {
  const map = loadCreds();
  const nk = norm(key);
  if (map[nk]) {
    delete map[nk];
    saveCreds(map);
    return true;
  }
  return false;
}

// Retire tous les comptes mémorisés hors-ligne de cet appareil.
export function forgetAllCredentials() {
  const count = Object.keys(loadCreds()).length;
  try { localStorage.removeItem(CREDS_KEY); } catch (e) {}
  return count;
}

// Reconnexion SILENCIEUSE : rafraîchit le jeton via /auth/login en réutilisant le mot de passe
// chiffré localement (AES-GCM). Utilisé au retour du réseau après une coupure > durée de vie du jeton.
// Renvoie true si un nouveau jeton a été obtenu.
export async function attemptSilentRelogin() {
  // 1) Essai prioritaire : rafraîchir le jeton d'accès via le jeton de rafraîchissement (compatible 2FA)
  try {
    const rt = secureStorage.getRefreshToken();
    if (rt) {
      const r = await fetch(`${BACKEND_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
      });
      if (r.ok) {
        const d = await r.json();
        if (d?.access_token) {
          secureStorage.setToken(d.access_token);
          if (d.refresh_token) secureStorage.setRefreshToken(d.refresh_token);
          console.log('🔐 Jeton d\'accès rafraîchi (refresh token)');
          return true;
        }
      }
    }
  } catch {}

  return false;
}

// Mémorise le compte après une connexion EN LIGNE réussie.
export async function rememberCredentials(username, password, user) {
  if (!subtle()) return; // contexte non sécurisé : hors-ligne non disponible
  try {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const hash = await pbkdf2(password, salt, ITERATIONS);
    const map = loadCreds();
    map[norm(username)] = {
      username: (username || '').trim(),
      salt: bufToHex(salt),
      iterations: ITERATIONS,
      hash,
      user,
      savedAt: Date.now(),
    };
    saveCreds(map);
  } catch {}
}

// Vérifie des identifiants HORS-LIGNE. Renvoie { user, token } si OK, sinon null.
export async function verifyOffline(username, password) {
  const map = loadCreds();
  const rec = map[norm(username)];
  if (!rec) return null;
  try {
    const hash = await pbkdf2(password, hexToBuf(rec.salt), rec.iterations || ITERATIONS);
    if (hash === rec.hash) return { user: rec.user, token: 'offline' };
  } catch {}
  return null;
}
